/**
 * dataApi.ts — the Data tab's transport.
 *
 * Every call targets the project REST surface (`/api/projects/{id}/tables*`
 * and `/sql/*`) — the same seam the View tab's TablesPanel uses, which talks
 * to the project's VSKI-platform tables interchangeably. The helper adds the
 * bearer token when present (the SQL routes didn't send one before) and
 * normalises errors to thrown `Error`s carrying the backend `detail`, so the
 * panel can surface them inline.
 */

export type CellValue = string | number | boolean | null;

export interface DataColumn {
  name: string;
  type: string;
  /** NOT NULL without a default — empty values are rejected on insert. */
  required?: boolean;
  default?: unknown;
}

export interface DataRow {
  id: number | string;
  cells: Record<string, CellValue>;
}

export interface TableSummary {
  name: string;
  columns: DataColumn[];
  count?: number;
}

export interface TablePage {
  table: string;
  columns: DataColumn[];
  rows: DataRow[];
  total: number;
}

export interface SqlResult {
  type: 'table' | 'mutation' | 'error';
  query_id?: string;
  total_rows?: number;
  rowcount?: number | null;
  columns?: string[];
  error?: string;
}

export type DataOrigin = { origin: string; token?: string; projectId: string };

function headers(token: string | undefined, json = false): Record<string, string> {
  return {
    ...(json ? { 'content-type': 'application/json' } : {}),
    ...(token ? { authorization: `Bearer ${token}` } : {}),
  };
}

async function unwrap(r: Response, fallback: string): Promise<any> {
  if (r.ok) return r.json();
  let detail = fallback;
  try {
    const body = await r.json();
    detail = String(body.detail ?? body.message ?? fallback);
  } catch {
    /* keep fallback */
  }
  throw new Error(detail);
}

function base(o: DataOrigin): string {
  return `${o.origin}/api/projects/${encodeURIComponent(o.projectId)}`;
}

/** App-managed chat collections — internal plumbing (persisted chat
 *  history), never shown as project data. */
const HIDDEN_TABLES = new Set(['chat_threads', 'chat_messages']);

/** Drop the internal chat collections from a table list (the Data view's
 *  rail, the SQL schema sidebar, dataset pickers). Frontend-only by design —
 *  the rows stay fully visible in the platform's collections UI. */
export function visibleTables<T extends { name: string }>(tables: T[]): T[] {
  return tables.filter((t) => !HIDDEN_TABLES.has(t.name));
}

export async function listTables(o: DataOrigin): Promise<TableSummary[]> {
  const data = await unwrap(
    await fetch(`${base(o)}/tables`, { headers: headers(o.token) }),
    'Loading tables failed.',
  );
  // The route responds {"tables": […]} — unwrap, and stay defensive if the
  // body is ever malformed (the panel renders .map over this).
  const tables = Array.isArray(data?.tables) ? data.tables : [];
  return visibleTables(tables);
}

export async function readTablePage(
  o: DataOrigin,
  table: string,
  opts: { limit?: number; offset?: number; sort?: string; direction?: 'asc' | 'desc' } = {},
): Promise<TablePage> {
  const q = new URLSearchParams({
    limit: String(opts.limit ?? 30),
    offset: String(opts.offset ?? 0),
    sort: opts.sort ?? 'id',
    direction: opts.direction ?? 'asc',
  });
  return unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}?${q}`, { headers: headers(o.token) }),
    `Loading '${table}' failed.`,
  );
}

export async function createTable(
  o: DataOrigin,
  name: string,
  columns: { name: string; type: string; required?: boolean }[],
): Promise<unknown> {
  return unwrap(
    await fetch(`${base(o)}/tables`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ name, columns }),
    }),
    `Creating table '${name}' failed.`,
  );
}

export async function deleteTable(o: DataOrigin, table: string): Promise<void> {
  await unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}`, {
      method: 'DELETE',
      headers: headers(o.token),
    }),
    `Deleting table '${table}' failed.`,
  );
}

/** Add columns to an existing table (creates the table when missing) —
 *  ADD-ONLY evolution, mirrored into the project's data/tables.json. */
export async function addColumns(
  o: DataOrigin,
  table: string,
  columns: { name: string; type: string; required?: boolean }[],
): Promise<unknown> {
  return unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}/columns`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ columns }),
    }),
    `Adding columns to '${table}' failed.`,
  );
}

export async function insertRow(
  o: DataOrigin,
  table: string,
  cells: Record<string, CellValue>,
): Promise<unknown> {
  return unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ cells }),
    }),
    `Inserting into '${table}' failed.`,
  );
}

export async function updateCell(
  o: DataOrigin,
  table: string,
  rowId: number | string,
  column: string,
  value: CellValue,
): Promise<unknown> {
  return unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}/${rowId}`, {
      method: 'PATCH',
      headers: headers(o.token, true),
      body: JSON.stringify({ column, value }),
    }),
    `Updating ${table}.${column} failed.`,
  );
}

export async function deleteRow(o: DataOrigin, table: string, rowId: number | string): Promise<unknown> {
  return unwrap(
    await fetch(`${base(o)}/tables/${encodeURIComponent(table)}/${rowId}`, {
      method: 'DELETE',
      headers: headers(o.token),
    }),
    `Deleting the row failed.`,
  );
}

// ─── Events → flow-net graphs ───────────────────────────────────────────────

export interface GraphFromTablePayload {
  table: string;
  label: string;
  order: string;
  group?: string | null;
  /** The rate formula: 'count' | 'mean:<col>' | 'sum:<col>' | 'delta'. */
  weight?: string;
  capacity?: string | null;
  signal?: boolean;
  graph_id?: string | null;
}

export interface FlowsNodeSummary {
  id: string;
  visits: number;
}
export interface FlowsEdgeSummary {
  id: string;
  count: number;
  weight: number;
}
export interface FlowsPathSummary {
  nodes: string[];
  edges: string[];
  share: number;
}
export interface GraphFromTableResult {
  graphId: string;
  replaced: boolean;
  rows: number;
  nodes: FlowsNodeSummary[];
  edges: FlowsEdgeSummary[];
  topPaths: FlowsPathSummary[];
  note?: string;
}

/** Convert a table of labeled events into a flow-net graph (events →
 *  nodes, consecutive events within a group → edges). The backend lands
 *  it on the canvas via the same broadcast a chat edit gets. */
export async function graphFromTable(
  o: DataOrigin,
  payload: GraphFromTablePayload,
): Promise<GraphFromTableResult> {
  return unwrap(
    await fetch(`${base(o)}/graphs/from-table`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify(payload),
    }),
    'Converting the table failed.',
  );
}

// ─── SQL console ────────────────────────────────────────────────────────────

export async function sqlExecute(o: DataOrigin, query: string): Promise<SqlResult> {
  return unwrap(
    await fetch(`${base(o)}/sql/execute`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ query }),
    }),
    'Execution failed.',
  );
}

export async function sqlValidate(o: DataOrigin, query: string): Promise<{ valid: boolean; error?: string }> {
  return unwrap(
    await fetch(`${base(o)}/sql/validate`, {
      method: 'POST',
      headers: headers(o.token, true),
      body: JSON.stringify({ query }),
    }),
    'Validation failed.',
  );
}

export async function sqlResultRows(
  o: DataOrigin,
  queryId: string,
  limit: number,
  offset: number,
): Promise<Record<string, CellValue>[]> {
  const r = await unwrap(
    await fetch(`${base(o)}/sql/results/${queryId}?limit=${limit}&offset=${offset}`, {
      headers: headers(o.token),
    }),
    'Loading results failed.',
  );
  return r.rows ?? [];
}

export function sqlExportUrl(o: DataOrigin, queryId: string): string {
  return `${base(o)}/sql/export/${queryId}`;
}
